import React from 'react';
import { useReactToPrint } from 'react-to-print';
import { useKeyboardManagerState } from '../hooks/useKeyboardManagerState';
import { KeyboardShortcutState } from '../redux/types';
import { InputCaptureKeyboardShortcut } from './InputCaptureKeyboardShortcut';
import { StringWithMatches } from './StringWithMatches';
import { groupBy } from '../utils/groupBy';
import { useFullTextSearch, ResultItem } from '../hooks/useFullTextSearch';
import { getPotencialConflict } from '../redux/reducer';
import { Conflict } from '../redux/types';
import { Shortcut } from './Shortcut';
import { Toggle } from '../../../Components/Toggle';
import { Input } from '../../../Components/Input';
import { Button } from '../../../Components/Button';
import { Feedback } from '../../../Components/Feedback';
import { MIcon } from '../../../Components/Icon';
import { Dialog, DialogFooter } from '../../../Components/Dialog';
import { StatelessAccordion, Panel, PanelProps } from 'baseui/accordion';
import { shortcutToString } from '../utils/shortcutToString';

import { styled } from 'baseui';
import { borderStyle, marginSize, paddingSize } from '../../../Utilities/utils/Theming';
import { StyleObject } from 'styletron-standard';

const Aside = styled('aside', {
	overflowY: 'hidden',
	display: 'flex',
	flexDirection: 'column',
	color: 'var(--epods--color-grey-950)',

	// TODO: this needs to be integrated with the header panels
	height: '100%',
});

const ShortcutsScrollableContainer = styled('div', {
	overflowY: 'auto',
	flex: '1 1 auto',
	borderBottom: '1px solid var(--epods--color-bluegrey-100)',
	...paddingSize('0', 'var(--epods--space-inset-m)', '0', 'var(--epods--space-inset-m)'),
});

const ShortcutsList = styled('div', {
	borderTop: '1px solid var(--epods--color-bluegrey-100)'
});

const ShortcutsActions = styled('div', {
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	...paddingSize('var(--epods--space-inset-m)', 'var(--epods--space-inset-m)', '2px', 'var(--epods--space-inset-m)'),
	position: 'relative',
});

type GroupedItem = Record<string, ResultItem<KeyboardShortcutState>[]>;
const UnstyledList = styled('ul', {
	listStyleType: 'none',
	...marginSize('0', '0', '0', '0'),
	...paddingSize('0', '0', '0', '0'),
});

const StyledLabel = styled('label', {
	color: 'var(--epods--color-text-primary)',
	maxWidth: '151px',
})

const StyledHeading = styled('h2', {
	color: 'var(--epods--color-text-primary)',
});

const FeedbackWrapper = styled('div', {
	position: 'absolute',
	right: 'var(--epods--space-inset-m)',
	bottom: '60px',
	left: 'var(--epods--space-inset-m)',
});

export const CustomStatelessAccordion: React.FC<{
	expanded: React.Key[];
	setExpanded: (value: React.SetStateAction<React.Key[]>) => void;
}> = ({ children, expanded, setExpanded }) => {
	return (
		<StatelessAccordion
			accordion={false}
			expanded={expanded}
			onChange={({ key, expanded }) => {
				setExpanded(expanded);
			}}
			overrides={{
				Root: {
					style: {
						marginTop: 0,
						marginBottom: 0,
						marginLeft: 0,
						marginRight: 0,
					},
				},
				Content: {
					style: {
						...borderStyle('none'),
						...paddingSize('0', '0', '0', '0'),
						backgroundColor: 'none',
					} as StyleObject,
				},
				Header: {
					style: {
						flexDirection: 'row-reverse',
						justifyContent: 'flex-end',
						color: 'var(--epods--color-text-primary)',
						backgroundColor: 'var(--epods--color-bg)',

						fontSize: 'var(--epods--font-size-p)',
						lineHeight: 'var(--epods--line-height-p)',
						fontFamily: 'var(--epods--font-secondary-semibold)',
						':hover': {
							color: 'var(--epods--color-text-primary)',
						},
						...borderStyle('none'),
						...paddingSize('8px', '0', '8px', '0'),
					} as StyleObject,
				},
				ToggleIcon: {
					style: {
						marginRight: '4px',
					},
					// @ts-ignore
					component: (props: { $expanded?: boolean }) => {
						return (
							<MIcon
								size={'18px'}
								aria-hidden="true"
								icon={props.$expanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
								className={'mr-xs'}
							/>
						);
					},
				},
				PanelContainer: {
					style: ({ $theme }) => ({
						borderWidth: '1',
						borderColor: 'var(--epods--color-bluegrey-100)',
					})
				}
			}}
		>
			{children}
		</StatelessAccordion>
	);
};

export const CustomPanel: React.FC<PanelProps> = props => (
	<Panel
		{...props}
		onKeyDown={event => {
			const selfItem = event.target as HTMLDivElement | null;
			if (!selfItem) {
				return;
			}
			const selfLi = selfItem.closest('li');

			if (!selfLi) {
				return;
			}

			let elementToFocus: HTMLButtonElement | null = null;

			if (event.key === 'ArrowDown') {
				elementToFocus = selfLi.nextSibling?.firstChild as HTMLButtonElement;
			}
			if (event.key === 'ArrowUp') {
				elementToFocus = selfLi.previousSibling?.firstChild as HTMLButtonElement;
			}

			if (event.key === 'Home') {
				elementToFocus = selfLi.parentElement?.firstChild?.firstChild as HTMLButtonElement;
			}

			if (event.key === 'End') {
				elementToFocus = selfLi.parentElement?.lastChild?.firstChild as HTMLButtonElement;
			}

			if (elementToFocus) {
				elementToFocus.focus();
				event.preventDefault();
			}
		}}
	/>
);

export const ShortcutsPanel: React.FC<{
	id?: string;
	onClose?: () => any;
	children?: (args: { shortcutsList: React.ReactNode }) => any //React.ReactNode
}> = ({ id: parentId, onClose, children }) => {
	const ref = React.useRef<HTMLInputElement | null>(null);
	const { state, dispatch } = useKeyboardManagerState();
	const selfRef = React.useRef<HTMLDivElement | null>(null);
	const handlePrint = useReactToPrint({
		content: () => selfRef.current,
	});
	const [conflict, setConflict] = React.useState<Conflict | null>(null);

	const shortcuts = Object.values(state.shortcuts);
	const { results, inputProps } = useFullTextSearch<KeyboardShortcutState>({
		items: Object.values(state.shortcuts),
		key: 'name',
	});
	const groups = Array.from(new Set(shortcuts.map(s => s.group || 'General')));
	const [groupedResults, setGroupedResults] = React.useState<GroupedItem>({});
	const [savedNotification, setSavedNotification] = React.useState<string | null>(null);
	const [resetShortcut, setResetShortcut] = React.useState({ isReset: false, shortcutName: '' })
	const [resetAllShortcuts, setResetAllShortcuts] = React.useState<Boolean>(false);
	const [feedbackType, setFeedbackType] = React.useState<string | null>(null);
	React.useEffect(() => {
		const newGroupedResult = groupBy(results, i => i.item.group || 'General');
		setGroupedResults(newGroupedResult);

		setExpanded(Object.keys(newGroupedResult));
	}, [results]);

	/* Export shortcut functionalitty - second phase of development */

	// function exportShortcuts() {
	// 	downloadObjectAsJson('shortcuts.json', serializeShortcuts(state));
	// }

	/* Import shortcut functionalitty - second phase of development */

	// async function importShortcuts(event: React.ChangeEvent<HTMLInputElement>) {
	// 	const shortcuts = await fileChangeEventHandler<Record<string, { isActive: boolean; keys: string }>>(event);
	// 	if (shortcuts) {
	// 		dispatch({ type: 'IMPORT_SHORTCUTS', payload: { contents: shortcuts } });
	// 		// @ts-ignore
	// 		document.dispatchEvent(
	// 			new CustomEvent('ds-keyboard-shortcuts-import', {
	// 				bubbles: true, // bubble event to containing elements
	// 				composed: true, // let the event pass through the shadowDOM boundary
	// 				detail: {
	// 					id: parentId,
	// 					payload: { contents: shortcuts },
	// 				},
	// 			}),
	// 		);
	// 	}
	// }

	const resetAllShortcutsToDefault = () => {
		dispatch({ type: 'RESET_ALL_SHORTCUT_KEYS' });
		// @ts-ignore
		document.dispatchEvent(
			new CustomEvent('ds-keyboard-shortcuts-reset-all', {
				bubbles: true, // bubble event to containing elements
				composed: true, // let the event pass through the shadowDOM boundary
				detail: {
					id: parentId,
					payload: {},
				},
			}),
		);
		setResetAllShortcuts(true);
		setFeedbackType('resetAllShortcuts');
	};

	function conflictDiscard() {
		setConflict(null);
	}

	function focusOnShortcutResetButton(id: string) {
		setTimeout(() => {
			const resetButton = document.getElementById(`input-capture-reset-${id}`);
			resetButton?.focus();
		}, 100);
	}

	function conflictResolve() {
		if (!conflict) {
			return;
		}
		focusOnShortcutResetButton(conflict.id);

		dispatch({ type: 'OVERRIDE_CONNFLICT', payload: conflict });
		// @ts-ignore
		document.dispatchEvent(
			new CustomEvent('ds-keyboard-shortcuts-override-conflict', {
				bubbles: true, // bubble event to containing elements
				composed: true, // let the event pass through the shadowDOM boundary
				detail: {
					id: parentId,
					payload: { conflict },
				},
			}),
		);
		setConflict(null);
	}

	const [expanded, setExpanded] = React.useState<React.Key[]>(groups.map(group => group ?? 'General'));

	const shortcutsList = (
		<>
			<ShortcutsScrollableContainer className="py-m">
				{shortcuts.length > 0 ? (
					<ShortcutsList className="space-y-4 keyboard-shortcuts-list">
						<CustomStatelessAccordion expanded={expanded} setExpanded={setExpanded}>
							{groups.map((group, index) => (
								<CustomPanel key={group ?? 'General'} title={group ?? 'General'}>
									<UnstyledList role="list" className="pb-m">
										{(groupedResults[group] ?? []).map(({ item, matches }) => {
											const itemState = state.shortcuts[item.id];
											const { id, name, isDesactivatable, isOverridable, hasConflict } = item;
											return (
												<li key={id} className="pt-xs pl-s pb-s d-block">
													<div className="d-flex align-items-center">
														<StyledLabel
															className={'flex-grow-1 line-clamp-2 mr-s'}
															// htmlFor={`shortcut-is-in-page-${id}`} - duplicated label
															title={name}
														>
															{/* @ts-ignore */}
															<StringWithMatches text={name} matches={matches} />
														</StyledLabel>
														<InputCaptureKeyboardShortcut
															state={state.shortcuts[item.id]}
															aria-label="Record shortcut now"
															aria-live="assertive"
															className="mr-m"
															isOverridable={isOverridable}
															hasConflict={
																conflict?.id === id ||
																conflict?.conflictingKey === id
															}
															resetButtonProps={{
																id: `input-capture-reset-${id}`,
																onClick: () => {
																	dispatch({
																		type: 'RESET_SHORTCUT_KEYS',
																		payload: { id },
																	});
																	// @ts-ignore
																	document.dispatchEvent(
																		new CustomEvent(
																			'ds-keyboard-shortcuts-reset-keys',
																			{
																				bubbles: true, // bubble event to containing elements
																				composed: true, // let the event pass through the shadowDOM boundary
																				detail: {
																					id: parentId,
																					payload: { id },
																				},
																			},
																		),
																	);
																	const resetKey = { isReset: true, shortcutName: name }
																	setResetShortcut(prevState => ({
																		...prevState,
																		...resetKey
																	}));
																	setFeedbackType('resetShortcut');
																},
															}}
															onComplete={value => {
																const potencialConflict = getPotencialConflict(
																	state,
																	id,
																	value,
																);
																if (potencialConflict) {
																	setConflict({
																		type: 'KEYS',
																		id,
																		keys: value,
																		conflictingKey: potencialConflict,
																	});
																} else {
																	dispatch({
																		type: 'SET_SHORTCUT_KEYS',
																		payload: { id, keys: value },
																	});

																	// @ts-ignore
																	document.dispatchEvent(
																		new CustomEvent(
																			'ds-keyboard-shortcuts-set-keys',
																			{
																				bubbles: true, // bubble event to containing elements
																				composed: true, // let the event pass through the shadowDOM boundary
																				detail: {
																					id: parentId,
																					payload: {
																						id,
																						keys: value,
																					},
																				},
																			},
																		),
																	);
																	setSavedNotification(name);
																	setFeedbackType('changeShortcut');
																}
															}}
														/>
														<Toggle
															id={`shortcut-is-in-page-${id}`}
															name={`${id}-isInPage`}
															// @ts-ignore
															aria-label={`Disable/Enable shortcut: ${name}`} // a11y-TODO: disable/enable depending on state
															disabled={isDesactivatable === false}
															onChange={() => {
																const potencialConflict = getPotencialConflict(
																	state,
																	id,
																);
																if (potencialConflict) {
																	setConflict({
																		type: 'ACTIVE',
																		id,
																		conflictingKey: potencialConflict,
																	});
																} else {
																	dispatch({
																		type: 'TOGGLE_IS_ACTIVE',
																		payload: { id },
																	});

																	// @ts-ignore
																	document.dispatchEvent(
																		new CustomEvent(
																			'ds-keyboard-shortcuts-toggle-is-active',
																			{
																				bubbles: true, // bubble event to containing elements
																				composed: true, // let the event pass through the shadowDOM boundary
																				detail: {
																					id: parentId,
																					payload: { id },
																				},
																			},
																		),
																	);
																}
															}}
															checked={itemState.isActive}
															innerLabels={['On', 'Off']}
														>
															<span className="sr-only">Disable shortcut</span>
														</Toggle>
													</div>
													{hasConflict ? (
														<p className="h6 px-m">
															<strong className="text-warning">Warning:</strong>&nbsp; This new shortcut
															has been deactivated, as it conflicts with another
															shortcut.
														</p>
													) : null}
												</li>
											);
										})}
									</UnstyledList>
								</CustomPanel>
							))}
						</CustomStatelessAccordion>
					</ShortcutsList>
				) : (
					<p>No shortcuts</p>
				)}
				{results.length === 0 ? (
					<p>
						No shortcuts found with label <em>"{inputProps.value}"</em>.
					</p>
				) : null}
			</ShortcutsScrollableContainer>

			{conflict ? (
				<>
					<Dialog
						// we deal with it manually
						// @ts-ignore
						returnFocus={false}
						icon="warning"
						kind="warning"
						header={
							conflict.type === 'KEYS' ? (
								<>
									<Shortcut>{shortcutToString(conflict.keys)}</Shortcut> already used in another active shortcut.
								</>
							) : (
								<>
									A shortcut with keys{' '}
									<Shortcut>{state.shortcuts[conflict.conflictingKey].keys}</Shortcut> is already
									active.
								</>
							)
						}
						isOpen={true}
						onClose={() => {
							setConflict(null);
						}}
					>
						{conflict.type === 'KEYS' ? (
							<>
								<p>
									You can't have two active shortcuts with the same keys. The keys you recorded for{' '}
									<span className="font-semibold ">
										"{state.shortcuts[conflict.id]?.name}"
									</span>{' '}
									are already defined for{' '}
									<span className="font-semibold ">
										"{state.shortcuts[conflict.conflictingKey]?.name}"
									</span>
									.
								</p>
								<p>You can either <span className='font-semibold '>discard changes</span> or <span className='font-semibold '>disable "{state.shortcuts[conflict.conflictingKey]?.name}"</span></p>
							</>
						) : (
							<>
								<p>
									You can't have two active shortcuts with the same keys. You activated the{' '}
									<span className="font-semibold ">
										"{state.shortcuts[conflict.id]?.name}"
									</span>
									, which as the same keys as
									<span className="font-semibold ">
										"{state.shortcuts[conflict.conflictingKey]?.name}"
									</span>
									.
								</p>

								{state.shortcuts[conflict.conflictingKey]?.isDesactivatable === false ? (
									<>
										<p>
											As the shortcut{' '}
											<span className="font-semibold ">
												"{state.shortcuts[conflict.conflictingKey]?.name}"
											</span>{' '}
											can't be disabled, you need{' '}
											<span className="font-semibold ">Discard changes</span>.
										</p>

										<p>
											If you want to activate this shortcut, you first need to change its keys and
											activate it after.
										</p>
									</>
								) : (
									<p>
										You can either{' '}
										<span className="font-semibold ">discard changes</span> or{' '}
										<span className="font-semibold ">
											disable "{state.shortcuts[conflict.conflictingKey]?.name}"
										</span>
									</p>
								)}
							</>
						)}
						<DialogFooter>
							<Button onClick={conflictDiscard} theme="secondary" className="mr-s ml-auto">
								Discard changes
							</Button>
							{state.shortcuts[conflict.conflictingKey]?.isDesactivatable === false ? null : (
								<Button onClick={conflictResolve}>Disable conflicting shortcut</Button>
							)}
						</DialogFooter>
					</Dialog>
					<div className="fixed flex items-center justify-center top-0 left-0 right-0 bottom-0 bg-black bg-opacity-30">
						<div className="bg-gray-50 p-2 max-w-6xl bg-white shadow-md space-y-4"></div>
					</div>
				</>
			) : null}
		</>
	);

	const KSMFeedback = () => {
		const title = feedbackType === 'changeShortcut' ? `${savedNotification}` : (feedbackType === 'resetShortcut' ? `${resetShortcut.shortcutName}` : undefined);
		const text = feedbackType === 'resetAllShortcuts' ? 'All keyboard shorcuts have been reset to their default values.' : (feedbackType === 'resetShortcut' ? 'has been reset.' : (feedbackType === 'changeShortcut' ? 'has been changed.' : ''));
		return (
			<FeedbackWrapper>
				<Feedback
					displayTitleOnTooltip={resetAllShortcuts ? true : undefined}
					title={title}
					text={text}
					kind="positive"
					className="w-100"
					noEllipsis
					displayBlock
					autoHideDuration={4000}
					onClose={() => {
						if (feedbackType === 'changeShortcut') {
							setSavedNotification(null);
						} else if (feedbackType === 'resetShortcut') {
							setResetShortcut(prevState => ({
								...prevState,
								...{ isReset: false, shortcutName: '' }
							}));
						} else if (feedbackType === 'resetAllShortcuts') {
							setResetAllShortcuts(false);
						}
						setFeedbackType(null);
					}}
				/>
			</FeedbackWrapper>
		);
	};

	if (children) {
		return children({ shortcutsList });
	}

	return (
		<>
			<Aside ref={selfRef}>
				<div className="pb-m pb-m pt-0 px-m">
					<StyledHeading className="h3 mb-m mt-0">
						Keyboard Shortcuts
						<span className="screen-hide">{" "} - {parentId} </span>
					</StyledHeading>
					<div className="print-hide">
						<Input placeholder="Search shortcut actions" aria-label="Search shortcut actions" iconBefore="search" {...inputProps} />
					</div>
				</div>

				{shortcutsList}
				<div className="print-hide">
					<ShortcutsActions>
						{feedbackType && (
							<KSMFeedback />
						)}
						<div>
							<Button className="mr-s" onClick={resetAllShortcutsToDefault} theme="tertiary" iconBefore="reset">
								Reset all
							</Button>
							<Button className="" onClick={handlePrint} theme="tertiary">
								Print
							</Button>
						</div>
					</ShortcutsActions>
				</div>
			</Aside>
		</>
	);
};
